/**
 * Cost Explorer — the same consumption estimate as /billing/summary, sliced
 * by cost driver, template, person and how the work arrived.
 *
 * Nothing here is an allocation. The server prices every completed
 * document-analysis job (the billing ledger: no template set-up, no failed
 * runs) by one rule, src/pricing.py: priced pages x the per-page rate, plus
 * Content Understanding's own model tokens and the extraction tokens at the
 * per-million rates. This module prices exactly the same jobs, one at a time,
 * with the rates that endpoint returns, so the slices are measured per job and
 * add back up to the summary's own period total, which the page checks and
 * shows rather than asserting.
 *
 * What the job does not know (its template, who raised it, which door it came
 * in through) comes from the request it ran for, joined on the job's entity id.
 */

// ---------------------------------------------------------------------------
// Period bounds — must mirror resolve_period / custom_bounds in billing_report.py
// ---------------------------------------------------------------------------

const DAY_MS = 86_400_000

/** Whole UTC days a period button means: the last N, including today. */
export const ROLLING_DAYS = { week: 7, month: 30, '3m': 90, '6m': 180, year: 365 }

/** Midnight UTC, `back` days before the day containing `now`. */
function dayStartUtc(now, back) {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - back))
}

/**
 * The window the billing endpoints use for `range` (or a custom from/to).
 * `start`/`end` are Dates or null (unbounded). A button is the last N whole UTC
 * days including today, with the end left open: the slices here then cover the
 * same jobs as the reconciled total.
 */
export function periodBounds(range, custom = null, now = new Date()) {
  if (custom?.from && custom?.to) {
    const start = new Date(`${custom.from}T00:00:00Z`)
    const end = new Date(new Date(`${custom.to}T00:00:00Z`).getTime() + DAY_MS - 1)
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end >= start) {
      return { start, end }
    }
  }
  if (range === 'all' || range === 'max') return { start: null, end: null }
  const days = ROLLING_DAYS[range] || ROLLING_DAYS['3m']
  return { start: dayStartUtc(now, days - 1), end: null }
}

/** The backend serialises naive UTC datetimes without a zone; read them as UTC. */
export function parseUtc(iso) {
  if (!iso) return null
  const s = String(iso)
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * The viewer's timezone as a label, e.g. "IST (UTC+5:30)", "BST (UTC+1)",
 * "UTC+5:45". The heatmap buckets by the browser's clock, so the label says
 * which clock that is. Intl only gives a real abbreviation in some locales
 * ("IST" in en-IN, "BST" in en-GB, "EDT" in en-US); the rest say "GMT+5:30",
 * so try a few and fall back to the bare offset. `timeZone` defaults to the
 * browser's own.
 */
export function localZoneLabel(date = new Date(), timeZone = undefined) {
  const zoneName = (locale, style) => {
    try {
      return new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: style })
        .formatToParts(date).find(p => p.type === 'timeZoneName')?.value || null
    } catch { return null }
  }
  // "GMT+05:30" / "GMT" from longOffset; getTimezoneOffset only knows the browser's zone.
  const long = zoneName('en-US', 'longOffset')
  const m = long && /^GMT(?:([+-])(\d{2}):(\d{2}))?$/.exec(long)
  const minutes = m
    ? (m[1] ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) : 0)
    : -date.getTimezoneOffset()
  const abs = Math.abs(minutes)
  const offset = `UTC${minutes < 0 ? '-' : '+'}${Math.floor(abs / 60)}${abs % 60 ? `:${String(abs % 60).padStart(2, '0')}` : ''}`

  let abbr = null
  for (const locale of [undefined, 'en-GB', 'en-IN', 'en-US', 'en-AU']) {
    const name = zoneName(locale, 'short')
    // "GMT"/"UTC" without an offset is only an answer when the offset is zero.
    if (name && /^[A-Z]{2,5}$/.test(name) && (minutes === 0 || !/^(GMT|UTC)$/.test(name))) { abbr = name; break }
  }
  if (abbr === 'UTC') return 'UTC'
  return abbr ? `${abbr} (${offset})` : offset
}

export function inBounds(date, { start, end }) {
  if (!date) return start === null && end === null
  if (start && date < start) return false
  if (end && date > end) return false
  return true
}

// ---------------------------------------------------------------------------
// Pricing one job
// ---------------------------------------------------------------------------

export const DEFAULT_PRICING = {
  per_page_usd: 0.01,
  per_1m_input_tokens_usd: 2.5,
  per_1m_output_tokens_usd: 15,
  // Content Understanding runs a model of its own (GPT-5.2) and reports the tokens.
  cu_per_1m_input_tokens_usd: 1.75,
  cu_per_1m_output_tokens_usd: 14,
}

const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0)
/** The server's reading of a count: a whole number, never negative, junk is 0. */
const count = v => {
  const n = Math.trunc(Number(v))
  return Number.isFinite(n) && n > 0 ? n : 0
}

/** A job the billing ledger records: a completed document analysis. */
export function isBillableJob(job) {
  return job?.job_type === 'document_analysis' && String(job?.status || '').toLowerCase() === 'completed'
}

/**
 * What one job cost, split by driver. Reads the same keys, with the same
 * missing-means-zero rule, as the server (src/pricing.py, _usage_detail):
 *
 *   documents  priced pages x per-page rate + Content Understanding model tokens
 *              (`azure_cu_tokens`, keys containing "input" / "output"); priced
 *              pages are `azure_cu_standard_pages` when it is above 0, else
 *              `total_pages`
 *   extraction normalisation, email, LLM fallback and AI learning tokens at the
 *              per-million input / output rates
 *
 * A result that is not an object is skipped there and returns null here.
 * `pages` is the pages analysed (what the plan meters); the priced pages can
 * differ and are returned as `pricedPages`.
 */
export function priceJob(result, pricing = DEFAULT_PRICING) {
  if (!result || typeof result !== 'object' || Array.isArray(result)) return null
  const perPage = num(pricing.per_page_usd)
  const perIn = num(pricing.per_1m_input_tokens_usd) / 1_000_000
  const perOut = num(pricing.per_1m_output_tokens_usd) / 1_000_000
  const cuIn = num(pricing.cu_per_1m_input_tokens_usd) / 1_000_000
  const cuOut = num(pricing.cu_per_1m_output_tokens_usd) / 1_000_000

  const tokens = key => {
    const t = result[key]
    return t && typeof t === 'object' && !Array.isArray(t)
      ? { input: count(t.input), output: count(t.output) }
      : { input: 0, output: 0 }
  }
  const norm = tokens('azure_openai_normalisation_tokens')
  const email = tokens('openai_email_tokens')
  const llm = tokens('llm_fallback_tokens')
  const learn = tokens('ai_learning_tokens')

  let cuInput = 0
  let cuOutput = 0
  const cuTokens = result.azure_cu_tokens
  if (cuTokens && typeof cuTokens === 'object' && !Array.isArray(cuTokens)) {
    for (const [key, value] of Object.entries(cuTokens)) {
      if (String(key).includes('input')) cuInput += count(value)
      if (String(key).includes('output')) cuOutput += count(value)
    }
  }

  const pages = count(result.total_pages)
  const cuPages = count(result.azure_cu_standard_pages)
  const pricedPages = cuPages > 0 ? cuPages : pages

  const drivers = {
    documents: pricedPages * perPage + cuInput * cuIn + cuOutput * cuOut,
    normalisation: norm.input * perIn + norm.output * perOut,
    email: email.input * perIn + email.output * perOut,
    llm_fallback: llm.input * perIn + llm.output * perOut,
    ai_learning: learn.input * perIn + learn.output * perOut,
  }
  const ai = drivers.normalisation + drivers.email + drivers.llm_fallback + drivers.ai_learning
  return {
    pages,
    pricedPages,
    tokens: norm.input + norm.output + email.input + email.output + llm.input + llm.output + learn.input + learn.output,
    drivers,
    docCost: drivers.documents,
    aiCost: ai,
    total: drivers.documents + ai,
  }
}

// ---------------------------------------------------------------------------
// Dimensions
// ---------------------------------------------------------------------------

export const DIMENSIONS = [
  { value: 'driver', label: 'Cost driver', noun: 'driver' },
  { value: 'template', label: 'Template', noun: 'template' },
  { value: 'person', label: 'Person', noun: 'person' },
  { value: 'source', label: 'How it arrived', noun: 'channel' },
]

export const DRIVERS = [
  { key: 'documents', label: 'Document processing', plain: 'Pages read by Azure Content Understanding' },
  { key: 'normalisation', label: 'Field normalisation', plain: 'AI tidying extracted values into your field formats' },
  { key: 'email', label: 'Email analysis', plain: 'AI reading the email body itself' },
  { key: 'llm_fallback', label: 'LLM fallback', plain: 'AI second pass for fields the first read missed' },
  { key: 'ai_learning', label: 'AI learning', plain: 'AI working out rules from the corrections made in review' },
]

export const SOURCES = [
  { key: 'outlook', label: 'Outlook add-in', plain: 'Sent from Outlook with the Xtract add-in' },
  { key: 'webapp', label: 'Web app', plain: 'Created by someone signed in to Xtract' },
  { key: 'api', label: 'API', plain: 'Submitted by another system through the Xtract API' },
  { key: 'other', label: 'Other processing', plain: 'Jobs not tied to a request, such as template set-up' },
]

const OUTLOOK_NAME = /outlook|plug-?in|add-?in/i
const SERVICE_NAME = /^service principal\b/i
const EMAIL_FILE = /\.(eml|msg)$/i

/**
 * Decide which identities are applications rather than people. The admin user
 * list deliberately omits service-principal accounts, so "not in that list" is
 * the app signal; without the list, fall back to the synthetic display name the
 * backend gives app-only tokens.
 */
function isAppIdentity(request, humanIds) {
  if (request.created_by == null) return false
  if (humanIds) return !humanIds.has(String(request.created_by))
  return SERVICE_NAME.test(request.created_by_name || '') || OUTLOOK_NAME.test(request.created_by_name || '')
}

function looksLikeEmail(request) {
  return Boolean(request.email_id) || (request.document_names || []).some(n => EMAIL_FILE.test(n || ''))
}

/**
 * Classify every request's channel. There is no channel column, so this reads
 * the traces each entry point leaves:
 *   - API: only the public extraction API writes an external reference, and
 *     app identities that never carry email are API clients.
 *   - Outlook: the add-in calls with its own app identity and sends email
 *     content, so an app identity that has ever carried email is the add-in.
 *   - Web app: everything a signed-in person created.
 */
export function classifySources(requests, humanIds = null) {
  const outlookApps = new Set()
  for (const r of requests) {
    if (r.ref || !isAppIdentity(r, humanIds)) continue
    if (looksLikeEmail(r) || OUTLOOK_NAME.test(r.created_by_name || '')) outlookApps.add(String(r.created_by))
  }
  const out = new Map()
  for (const r of requests) {
    let source = 'webapp'
    if (r.ref) source = 'api'
    else if (isAppIdentity(r, humanIds)) source = outlookApps.has(String(r.created_by)) ? 'outlook' : 'api'
    out.set(String(r.id), source)
  }
  return out
}

// ---------------------------------------------------------------------------
// Time buckets
// ---------------------------------------------------------------------------

const DAY = DAY_MS

export function pickGrouping(start, end) {
  if (!start) return 'month'
  const span = ((end || new Date()).getTime() - start.getTime()) / DAY
  if (span <= 45) return 'day'
  if (span <= 200) return 'week'
  return 'month'
}

/** Monday 00:00 UTC of the week containing `d`. */
function weekStart(d) {
  const day = (d.getUTCDay() + 6) % 7
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day))
}

export function bucketKey(d, grouping) {
  if (grouping === 'day') return d.toISOString().slice(0, 10)
  if (grouping === 'week') return weekStart(d).toISOString().slice(0, 10)
  return d.toISOString().slice(0, 7)
}

function bucketLabel(key, grouping) {
  if (grouping === 'month') {
    const [y, m] = key.split('-').map(Number)
    const d = new Date(Date.UTC(y, m - 1, 1))
    return {
      label: d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' }),
      fullLabel: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    }
  }
  const d = new Date(`${key}T00:00:00Z`)
  const short = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
  if (grouping === 'week') {
    return { label: short, fullLabel: `Week of ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })}` }
  }
  return { label: short, fullLabel: d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) }
}

/** Every bucket in the window, including empty ones, so gaps read as gaps. */
export function buildBuckets(start, end, grouping, firstSeen) {
  const from = start || firstSeen
  if (!from) return []
  const stop = end || new Date()
  const keys = []
  let cursor
  if (grouping === 'month') cursor = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), 1))
  else if (grouping === 'week') cursor = weekStart(from)
  else cursor = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()))
  let guard = 0
  while (cursor <= stop && guard++ < 800) {
    keys.push(bucketKey(cursor, grouping))
    if (grouping === 'month') cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1))
    else cursor = new Date(cursor.getTime() + (grouping === 'week' ? 7 : 1) * DAY)
  }
  return keys.map(key => ({ key, ...bucketLabel(key, grouping) }))
}

// ---------------------------------------------------------------------------
// The model
// ---------------------------------------------------------------------------

function blankItem(key, label, extra = {}) {
  return {
    key, label, cost: 0, docCost: 0, aiCost: 0, pages: 0, tokens: 0,
    jobs: 0, requestIds: new Set(),
    drivers: { documents: 0, normalisation: 0, email: 0, llm_fallback: 0, ai_learning: 0 },
    series: {},
    ...extra,
  }
}

function finalise(map, total) {
  return [...map.values()]
    .map(({ requestIds, ...item }) => ({
      ...item,
      requests: requestIds.size,
      share: total > 0 ? item.cost / total : 0,
      perRequest: requestIds.size > 0 ? item.cost / requestIds.size : null,
    }))
    .filter(item => item.cost > 0 || item.jobs > 0)
    .sort((a, b) => b.cost - a.cost)
}

/**
 * Build everything the explorer renders.
 *
 * @param jobs      rows from /api/jobs (with result_data); only completed
 *                  document-analysis jobs are priced, like the server's ledger
 * @param requests  rows from /api/requests (any that the jobs reference)
 * @param users     rows from /api/admin/users, or null if unavailable
 * @param pricing   summary.estimated_costs.pricing
 * @param bounds    { start, end } from periodBounds
 */
export function buildExplorer({ jobs = [], requests = [], users = null, pricing = DEFAULT_PRICING, bounds = { start: null, end: null } }) {
  const humanIds = users ? new Set(users.map(u => String(u.id))) : null
  const userName = new Map((users || []).map(u => [String(u.id), u.username || u.email]))
  const userEmail = new Map((users || []).map(u => [String(u.id), u.email]))
  const reqById = new Map(requests.map(r => [String(r.id), r]))
  const sources = classifySources(requests, humanIds)

  const priced = []
  let firstSeen = null
  for (const job of jobs) {
    // Failed runs and template set-up are not billed, so they are not priced.
    if (!isBillableJob(job)) continue
    // The ledger buckets a job by when it completed, so the window does too.
    const at = parseUtc(job.completed_at) || parseUtc(job.created_at)
    if (!inBounds(at, bounds)) continue
    const cost = priceJob(job.result_data, pricing)
    if (!cost) continue
    if (at && (!firstSeen || at < firstSeen)) firstSeen = at
    priced.push({ job, at, cost })
  }

  const grouping = pickGrouping(bounds.start || firstSeen, bounds.end)
  const buckets = buildBuckets(bounds.start, bounds.end, grouping, firstSeen)

  const dims = { driver: new Map(), template: new Map(), person: new Map(), source: new Map() }
  DRIVERS.forEach(d => dims.driver.set(d.key, blankItem(d.key, d.label, { plain: d.plain })))
  SOURCES.forEach(s => dims.source.set(s.key, blankItem(s.key, s.label, { plain: s.plain })))

  const totals = { cost: 0, docCost: 0, aiCost: 0, pages: 0, tokens: 0, jobs: 0, requestIds: new Set(), unattributed: 0 }
  const perBucket = new Map(buckets.map(b => [b.key, { cost: 0, requestIds: new Set() }]))
  const heat = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => ({ jobs: 0, cost: 0 })))
  const requestRows = new Map()

  const add = (item, entry, bucket, requestId) => {
    const { cost } = entry
    item.cost += cost.total
    item.docCost += cost.docCost
    item.aiCost += cost.aiCost
    item.pages += cost.pages
    item.tokens += cost.tokens
    item.jobs += 1
    if (requestId) item.requestIds.add(requestId)
    for (const k of Object.keys(item.drivers)) item.drivers[k] += cost.drivers[k]
    if (bucket) item.series[bucket] = (item.series[bucket] || 0) + cost.total
  }

  for (const entry of priced) {
    const { job, at, cost } = entry
    const isRequest = job.entity_type === 'request'
    const requestId = isRequest && job.entity_id != null ? String(job.entity_id) : null
    const req = requestId ? reqById.get(requestId) : null
    const bucket = at ? bucketKey(at, grouping) : null

    totals.cost += cost.total
    totals.docCost += cost.docCost
    totals.aiCost += cost.aiCost
    totals.pages += cost.pages
    totals.tokens += cost.tokens
    totals.jobs += 1
    if (requestId) totals.requestIds.add(requestId)
    if (requestId && !req) totals.unattributed += cost.total

    const pb = bucket && perBucket.get(bucket)
    if (pb) { pb.cost += cost.total; if (requestId) pb.requestIds.add(requestId) }

    if (at) {
      // Local time: "when does the work happen" is a question about the
      // viewer's working day, not about UTC.
      const cell = heat[(at.getDay() + 6) % 7][at.getHours()]
      cell.jobs += 1
      cell.cost += cost.total
    }

    // Driver: one job contributes to every driver it used.
    for (const d of DRIVERS) {
      const part = cost.drivers[d.key]
      if (part <= 0) continue
      const item = dims.driver.get(d.key)
      item.cost += part
      item.jobs += 1
      if (requestId) item.requestIds.add(requestId)
      item.drivers[d.key] += part
      if (d.key === 'documents') { item.docCost += part; item.pages += cost.pages } else item.aiCost += part
      if (bucket) item.series[bucket] = (item.series[bucket] || 0) + part
    }

    // Template
    let tKey; let tLabel
    if (!isRequest) { tKey = 'other'; tLabel = 'Other processing' }
    else if (!req) { tKey = 'unknown'; tLabel = 'Request not found' }
    else if (req.template_id == null) { tKey = 'none'; tLabel = 'No template (dynamic extraction)' }
    else { tKey = String(req.template_id); tLabel = req.template_name || `Template #${req.template_id}` }
    if (!dims.template.has(tKey)) dims.template.set(tKey, blankItem(tKey, tLabel, { templateId: req?.template_id ?? null }))
    add(dims.template.get(tKey), entry, bucket, requestId)

    // Person — who raised the request, falling back to who ran the job.
    const personId = req?.created_by ?? job.created_by
    const pKey = personId != null ? String(personId) : 'unknown'
    if (!dims.person.has(pKey)) {
      const isApp = personId != null && (humanIds ? !humanIds.has(pKey) : SERVICE_NAME.test(req?.created_by_name || ''))
      const label = userName.get(pKey) || req?.created_by_name || (personId != null ? `User #${personId}` : 'Unknown')
      dims.person.set(pKey, blankItem(pKey, label, { email: userEmail.get(pKey) || null, isApp }))
    }
    add(dims.person.get(pKey), entry, bucket, requestId)

    // Source
    const sKey = !isRequest ? 'other' : (req ? sources.get(requestId) : 'other')
    add(dims.source.get(sKey), entry, bucket, requestId)

    // Request rows, for the "most expensive" table and drill-downs.
    if (requestId) {
      if (!requestRows.has(requestId)) {
        requestRows.set(requestId, {
          id: requestId,
          title: req?.title || `Request #${requestId}`,
          templateKey: tKey,
          templateName: tLabel,
          personKey: pKey,
          personName: dims.person.get(pKey).label,
          source: sKey,
          status: req?.status || null,
          createdAt: req?.created_at || job.created_at,
          cost: 0, pages: 0, tokens: 0, runs: 0,
          drivers: { documents: 0, normalisation: 0, email: 0, llm_fallback: 0, ai_learning: 0 },
          known: Boolean(req),
        })
      }
      const row = requestRows.get(requestId)
      row.cost += cost.total
      row.pages += cost.pages
      row.tokens += cost.tokens
      row.runs += 1
      for (const k of Object.keys(row.drivers)) row.drivers[k] += cost.drivers[k]
    }
  }

  const total = totals.cost
  const unitSeries = buckets.map(b => {
    const pb = perBucket.get(b.key)
    const n = pb.requestIds.size
    return { ...b, cost: pb.cost, requests: n, perRequest: n > 0 ? pb.cost / n : null }
  })

  return {
    grouping,
    buckets,
    totals: {
      cost: total,
      docCost: totals.docCost,
      aiCost: totals.aiCost,
      pages: totals.pages,
      tokens: totals.tokens,
      jobs: totals.jobs,
      requests: totals.requestIds.size,
      perRequest: totals.requestIds.size > 0 ? total / totals.requestIds.size : null,
      unattributed: totals.unattributed,
    },
    dims: {
      driver: finalise(dims.driver, total),
      template: finalise(dims.template, total),
      person: finalise(dims.person, total),
      source: finalise(dims.source, total),
    },
    unitSeries,
    heat,
    requests: [...requestRows.values()].sort((a, b) => b.cost - a.cost),
  }
}

/**
 * Rows for a stacked chart: the top `limit` items keep their own series and
 * the rest fold into "Other", so a long tail of templates does not turn the
 * chart into confetti.
 */
export function chartSeries(items, buckets, limit = 6) {
  const visible = items.slice(0, limit)
  const rest = items.slice(limit)
  const series = visible.map(i => ({ key: `s_${i.key}`, itemKey: i.key, label: i.label }))
  if (rest.length) series.push({ key: 's__other', itemKey: '__other', label: `Other (${rest.length})` })
  const rows = buckets.map(b => {
    const row = { bucket: b.key, label: b.label, fullLabel: b.fullLabel }
    visible.forEach(i => { row[`s_${i.key}`] = i.series[b.key] || 0 })
    if (rest.length) row.s__other = rest.reduce((sum, i) => sum + (i.series[b.key] || 0), 0)
    return row
  })
  return { series, rows }
}

/** True when the slices add back to the summary's figure, within rounding. */
export function reconciles(explorerTotal, summaryTotal) {
  const a = Number(explorerTotal) || 0
  const b = Number(summaryTotal) || 0
  return Math.abs(a - b) <= Math.max(0.02, b * 0.005)
}
